import React, { useState, useEffect, useRef } from "react";
import { loadFactures, saveFacture, deleteFacture } from "../config/firestore";

function fmt(n) {
  return Number(n || 0).toFixed(2).replace(".", ",") + " €";
}
function fmtDate(s) {
  return new Date(s).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "2-digit" });
}
function todayISO() {
  return new Date().toISOString().split("T")[0];
}

const FACTURE_CATS = [
  "Garage", "Contrôle technique", "Assurance",
  "Carburant", "Péage", "Camping / Aire",
  "Matériel", "Eau / Gaz", "Électricité", "Autres",
];

const C = {
  bg:         "#C4622D",
  forest:     "#2D5A3D",
  dark:       "#1C3A28",
  text:       "#1C1C1E",
  muted:      "#8A8A8E",
  border:     "#D3D1C7",
};

function getStatus(dateStr) {
  if (!dateStr) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const exp   = new Date(dateStr);
  const days  = Math.round((exp - today) / 86400000);
  if (days < 0)   return { label: "Expiré",   color: "#C4622D", bg: "#FEF2F2", days };
  if (days <= 30) return { label: `${days} j`, color: "#EA7B20", bg: "#FEF3E8", days };
  if (days <= 60) return { label: `${days} j`, color: "#CA8A04", bg: "#FEFCE8", days };
  return              { label: "À jour",   color: "#2D9E4A", bg: "#ECFDF5", days };
}

function ValidateButton({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        marginTop: 6, padding: "7px 14px",
        borderRadius: 8, border: "none",
        background: "#2D5A3D", color: "#F8F6F1",
        fontSize: 12, fontWeight: 700,
        cursor: "pointer", fontFamily: "inherit",
      }}
    >
      ✓ Valider
    </button>
  );
}

function Field({ label, value, onChange, onValidate, dirty = false, type = "text", placeholder }) {
  const [isDirty, setIsDirty] = useState(false);
  const showDirty = dirty || isDirty;

  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: 0.8, marginBottom: 5 }}>
        {label.toUpperCase()}
      </div>
      <input
        type={type}
        value={value}
        onChange={e => {
          setIsDirty(true);
          onChange(e.target.value);
        }}
        placeholder={placeholder}
        style={{
          width: "100%", padding: "10px 12px",
          borderRadius: 10, border: `1.5px solid ${C.border}`,
          background: "white", color: C.text,
          fontSize: 14, outline: "none",
          fontFamily: "inherit", boxSizing: "border-box",
        }}
      />
      {showDirty && (
        <ValidateButton
          onClick={() => {
            onValidate?.();
            setIsDirty(false);
          }}
        />
      )}
    </div>
  );
}

function CallButton({ tel, label }) {
  return (
    <a
      href={tel ? `tel:${tel}` : undefined}
      style={{
        display: "block", padding: "16px 0",
        borderRadius: 14,
        background: tel ? "#EAF3DE" : C.bg,
        color: tel ? C.forest : C.muted,
        fontSize: 16, fontWeight: 800,
        textAlign: "center", textDecoration: "none",
        letterSpacing: 0.3, marginBottom: 10,
        cursor: tel ? "pointer" : "default",
        pointerEvents: tel ? "auto" : "none",
        border: `1.5px solid ${tel ? C.forest + "40" : C.border}`,
      }}
    >
      📞 {label}
    </a>
  );
}

function CoordCard({ title, summary, open, onToggle, onSave, children, t = {} }) {
  return (
    <div style={{
      borderRadius: 14, overflow: "hidden",
      boxShadow: "0 1px 8px rgba(44,90,61,0.07)",
      marginBottom: 10,
    }}>
      <button
        onClick={onToggle}
        style={{
          width: "100%", display: "flex", alignItems: "center",
          justifyContent: "space-between", padding: "14px 16px",
          background: C.dark, border: "none", cursor: "pointer", fontFamily: "inherit",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 800, color: "#F8F6F1", letterSpacing: 0.5 }}>
            {title.toUpperCase()}
          </span>
          {!open && summary && (
            <span style={{ fontSize: 11, color: "rgba(248,246,241,0.5)" }}>
              {summary}
            </span>
          )}
        </div>
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none"
          style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s", flexShrink: 0 }}
        >
          <path d="M2 4L6 8L10 4" stroke="rgba(248,246,241,0.55)"
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div style={{
          background: "white", padding: "16px",
          borderLeft: `2px solid ${C.dark}`,
          borderRight: `2px solid ${C.dark}`,
          borderBottom: `2px solid ${C.dark}`,
          borderRadius: "0 0 14px 14px",
        }}>
          {children}
          <button
            onClick={onSave}
            style={{
              width: "100%", padding: "13px 0",
              borderRadius: 10, border: "none",
              background: C.forest, color: "#F8F6F1",
              fontSize: 14, fontWeight: 800,
              cursor: "pointer", fontFamily: "inherit",
              letterSpacing: 0.3, marginTop: 4,
            }}
          >
            {t.enregistrer || "Enregistrer"}
          </button>
        </div>
      )}
    </div>
  );
}

function DateCard({ label, dateVal, onChange, onValidate, dirty = false, status, t = {} }) {
  const [isDirty, setIsDirty] = useState(false);
  const showDirty = dirty || isDirty;

  return (
    <div style={{
      background: "white", borderRadius: 14, padding: "16px",
      boxShadow: "0 1px 8px rgba(44,90,61,0.07)",
      border: `1.5px solid ${status ? status.color + "28" : "rgba(44,90,61,0.08)"}`,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: C.dark, letterSpacing: 0.5 }}>
          {label.toUpperCase()}
        </div>
        {status && (
          <div style={{
            background: status.bg, color: status.color,
            borderRadius: 20, padding: "3px 10px",
            fontSize: 11, fontWeight: 800,
          }}>
            {status.label === "Expiré" ? (t.expire || "Expiré")
             : status.label === "À jour" ? (t.aJour || "À jour")
             : status.label}
          </div>
        )}
      </div>
      <input
        type="date"
        value={dateVal}
        onChange={e => {
          setIsDirty(true);
          onChange(e.target.value);
        }}
        style={{
          width: "100%", padding: "10px 12px",
          borderRadius: 10, border: `1.5px solid ${C.border}`,
          background: "#F8F6F1", color: C.text,
          fontSize: 13, outline: "none",
          fontFamily: "inherit", boxSizing: "border-box",
        }}
      />
      {showDirty && (
        <ValidateButton
          onClick={() => {
            onValidate?.();
            setIsDirty(false);
          }}
        />
      )}
      <div style={{
        fontSize: 11, marginTop: 8, fontWeight: 600,
        color: !dateVal ? C.muted : status?.days < 0 ? "#C4622D" : status ? status.color : C.muted,
      }}>
        {!dateVal
          ? (t.aucuneDateRappel || "Aucune date — le rappel n'apparaîtra pas sur l'Accueil")
          : status?.days < 0
            ? (t.docExpire || "Document expiré — renouvelez-le dès que possible")
            : `${status.days}${t.joursRestants || " jour(s) restant(s) — rappel actif sur l'Accueil"}`
        }
      </div>
    </div>
  );
}

/* ── Compression image (canvas → JPEG) ──────────────────── */
function compressImage(dataUrl, maxPx = 1200, quality = 0.65) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const scale  = Math.min(1, maxPx / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width  = Math.round(img.width  * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      const out = canvas.toDataURL("image/jpeg", quality);
      console.log("[Compress]", Math.round(dataUrl.length / 1024), "Ko →", Math.round(out.length / 1024), "Ko");
      resolve(out);
    };
    img.src = dataUrl;
  });
}

/* ── Onglet Factures ─────────────────────────────────────── */
function TabFactures({ depenses, userId, t = {} }) {
  const fileRef              = useRef(null);
  const [viewItem, setViewItem] = useState(null); // { img, date, categorie }
  const [factures, setFactures] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [form, setForm] = useState({ date: todayISO(), categorie: FACTURE_CATS[0], img: null });
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  useEffect(() => {
    if (confirmDeleteId === null) return;
    const timer = setTimeout(() => setConfirmDeleteId(null), 3000);
    return () => clearTimeout(timer);
  }, [confirmDeleteId]);

  /* ── Chargement ── */
  useEffect(() => {
    let cancelled = false;

    async function fetch() {
      setLoading(true);
      try {
        // 1. localStorage (cache local)
        const localMap = {};
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (!key?.startsWith("checkvan_facture_")) continue;
          const id = Number(key.replace("checkvan_facture_", ""));
          if (isNaN(id)) continue;
          const img = localStorage.getItem(key);
          if (img) localMap[id] = img;
        }
        console.log("[TabFactures] localStorage:", Object.keys(localMap).length, "entrée(s)");

        // 2. Firestore (source principale)
        const fsItems = await loadFactures(userId);
        console.log("[TabFactures] Firestore:", fsItems.length, "entrée(s)");

        if (cancelled) return;

        // 3. Fusion : Firestore prioritaire, localStorage en complément
        const merged = {};
        fsItems.forEach(f => {
          merged[f.factureId] = { img: f.image, date: f.date, categorie: f.categorie };
        });
        Object.keys(localMap).forEach(idStr => {
          const id = Number(idStr);
          if (!merged[id]) {
            merged[id] = { img: localMap[id], date: "", categorie: "" };
          }
        });

        const list = Object.entries(merged)
          .map(([idStr, data]) => ({ id: Number(idStr), ...data }))
          .sort((a, b) => {
            const da = a.date || "", db = b.date || "";
            return db > da ? 1 : db < da ? -1 : b.id - a.id;
          });

        console.log("[TabFactures] Total affiché:", list.length);
        setFactures(list);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetch();
    return () => { cancelled = true; };
  }, [userId]);

  /* ── Scan photo (avec compression automatique) ── */
  const handleScan = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const compressed = await compressImage(ev.target.result);
      setForm(p => ({ ...p, img: compressed }));
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  /* ── Enregistrer une nouvelle facture ── */
  const handleSave = async () => {
    if (!form.img) return;
    setSaving(true);
    const fid = Date.now();
    // Firestore en premier (source principale, illimitée)
    await saveFacture(userId, fid, form.img, { date: form.date, categorie: form.categorie });
    // localStorage en cache best-effort (peut échouer si plein — pas bloquant)
    try {
      localStorage.setItem("checkvan_facture_" + fid, form.img);
    } catch {
      console.warn("[Save] localStorage plein — facture sauvée dans Firestore uniquement");
    }
    // Mise à jour locale instantanée
    const newItem = { id: fid, img: form.img, date: form.date, categorie: form.categorie };
    setFactures(prev =>
      [newItem, ...prev].sort((a, b) => {
        const da = a.date || "", db = b.date || "";
        return db > da ? 1 : db < da ? -1 : b.id - a.id;
      })
    );
    setForm({ date: todayISO(), categorie: FACTURE_CATS[0], img: null });
    setShowForm(false);
    setSaving(false);
  };

  /* ── Supprimer (deux clics) ── */
  const handleDelete = async (id) => {
    if (confirmDeleteId !== id) {
      setConfirmDeleteId(id);
      return;
    }
    setConfirmDeleteId(null);
    setFactures(prev => prev.filter(f => f.id !== id));
    if (viewItem?.id === id) setViewItem(null);
    await deleteFacture(userId, id);
  };

  /* ── Calcul stockage ── */
  const totalBytes   = factures.reduce((sum, f) => sum + Math.round((f.img?.length || 0) * 0.75), 0);
  const maxBytes     = 25 * 1024 * 1024; // 25 MB limite affichée
  const storagePct   = Math.min(100, Math.round(totalBytes / maxBytes * 100));
  const storageMB    = (totalBytes / 1024 / 1024).toFixed(1);
  const storageColor = storagePct >= 90 ? "#DC2626" : storagePct >= 80 ? "#EA7B20" : "#2D9E4A";
  const storageFull  = storagePct >= 80;

  /* ── Rendu ── */
  return (
    <div style={{ padding: "14px 16px 0" }}>

      {/* Input caché pour la caméra */}
      <input
        ref={fileRef} type="file"
        accept="image/*" capture="environment"
        style={{ display: "none" }}
        onChange={handleScan}
      />

      {/* Modal plein écran */}
      {viewItem && (
        <div
          onClick={() => setViewItem(null)}
          style={{
            position: "fixed", inset: 0, zIndex: 999,
            background: "rgba(0,0,0,0.92)",
            display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center",
            padding: 16,
          }}
        >
          <div onClick={e => e.stopPropagation()} style={{ position: "relative", maxWidth: 400, width: "100%" }}>
            {/* Bandeau métadonnées */}
            {(viewItem.categorie || viewItem.date) && (
              <div style={{
                background: C.dark, borderRadius: "12px 12px 0 0",
                padding: "10px 16px",
                display: "flex", justifyContent: "space-between", alignItems: "center",
              }}>
                <span style={{ fontSize: 13, fontWeight: 800, color: "#F8F6F1" }}>
                  {viewItem.categorie || "Facture"}
                </span>
                {viewItem.date && (
                  <span style={{ fontSize: 12, color: "rgba(248,246,241,0.65)" }}>
                    {fmtDate(viewItem.date)}
                  </span>
                )}
              </div>
            )}
            <img
              src={viewItem.img}
              alt="Facture"
              style={{
                width: "100%", display: "block",
                borderRadius: (viewItem.categorie || viewItem.date) ? "0 0 12px 12px" : 12,
              }}
            />
            {/* Fermer */}
            <button
              onClick={() => setViewItem(null)}
              style={{
                position: "absolute", top: -14, right: -14,
                background: "white", border: "none", borderRadius: "50%",
                width: 32, height: 32, fontSize: 14, cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
                boxShadow: "0 2px 8px rgba(0,0,0,0.35)",
              }}
            >✕</button>
            {/* Supprimer */}
            <button
              onClick={() => handleDelete(viewItem.id)}
              style={{
                position: "absolute", top: -14, left: -14,
                background: confirmDeleteId === viewItem.id ? "#DC2626" : "#FEF2F2",
                border: "none", borderRadius: confirmDeleteId === viewItem.id ? 10 : "50%",
                width: confirmDeleteId === viewItem.id ? "auto" : 32,
                height: 32, padding: confirmDeleteId === viewItem.id ? "0 10px" : 0,
                fontSize: confirmDeleteId === viewItem.id ? 11 : 14,
                cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
                boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
                color: confirmDeleteId === viewItem.id ? "white" : "#DC2626",
                fontWeight: 700, fontFamily: "inherit",
                whiteSpace: "nowrap",
                transition: "all 0.15s",
              }}
            >
              {confirmDeleteId === viewItem.id ? (t.confirmerSuppression || "Confirmer ?") : "🗑"}
            </button>
          </div>
        </div>
      )}

      {/* Bouton ajouter */}
      {!showForm && (
        <button
          onClick={() => setShowForm(true)}
          style={{
            width: "100%", padding: "13px 0", borderRadius: 12, border: "none",
            background: C.dark, color: "#F8F6F1",
            fontSize: 14, fontWeight: 800, cursor: "pointer",
            fontFamily: "inherit", marginBottom: 12, letterSpacing: 0.3,
          }}
        >
          {t.scanner || "+ Scanner une facture"}
        </button>
      )}

      {/* Barre de stockage */}
      {factures.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 5 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.8)" }}>
              {factures.length} {factures.length > 1 ? "photos" : "photo"}
            </span>
            <span style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.8)" }}>
              {storageMB} MB / 25 MB
            </span>
          </div>
          <div style={{ height: 6, background: "rgba(255,255,255,0.2)", borderRadius: 99, overflow: "hidden" }}>
            <div style={{
              height: "100%", width: `${storagePct}%`,
              background: storageColor,
              borderRadius: 99, transition: "width 0.4s ease",
            }} />
          </div>
          {storageFull && (
            <div style={{ fontSize: 11, color: "#FCA5A5", marginTop: 5, fontWeight: 600 }}>
              {t.stockagePlein || "⚠️ Stockage presque plein — supprimez des photos"}
            </div>
          )}
          {storageFull && (
            <button
              onClick={() => {
                const keys = [];
                for (let i = 0; i < localStorage.length; i++) {
                  const key = localStorage.key(i);
                  if (key?.startsWith("checkvan_facture_")) keys.push(key);
                }
                keys.forEach(k => localStorage.removeItem(k));
                alert(`${keys.length} photo(s) retirées du cache local.\nLes factures restent sauvegardées dans Firestore.`);
              }}
              style={{
                marginTop: 8, padding: "6px 12px",
                borderRadius: 8, border: "1px solid rgba(255,255,255,0.3)",
                background: "transparent", color: "rgba(255,255,255,0.75)",
                fontSize: 11, fontWeight: 600, cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              🧹 {t.viderCache || "Vider le cache local"}
            </button>
          )}
        </div>
      )}

      {/* Formulaire d'ajout */}
      {showForm && (
        <div style={{
          background: "white", borderRadius: 14, padding: 16,
          marginBottom: 14, boxShadow: "0 1px 8px rgba(0,0,0,0.08)",
        }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: C.dark, marginBottom: 14 }}>
            {t.nouvelleFacture || "Nouvelle facture"}
          </div>

          {/* Date */}
          <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: 0.8, marginBottom: 5 }}>{t.date || "DATE"}</div>
          <input
            type="date" value={form.date}
            onChange={e => setForm(p => ({ ...p, date: e.target.value }))}
            style={{
              width: "100%", padding: "10px 12px", marginBottom: 12,
              borderRadius: 10, border: `1.5px solid ${C.border}`,
              background: "#F8F6F1", color: C.text, fontSize: 14,
              outline: "none", fontFamily: "inherit", boxSizing: "border-box",
            }}
          />

          {/* Catégorie */}
          <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: 0.8, marginBottom: 5 }}>{t.categorie || "CATÉGORIE"}</div>
          <select
            value={form.categorie}
            onChange={e => setForm(p => ({ ...p, categorie: e.target.value }))}
            style={{
              width: "100%", padding: "10px 12px", marginBottom: 12,
              borderRadius: 10, border: `1.5px solid ${C.border}`,
              background: "#F8F6F1", color: C.text, fontSize: 14,
              outline: "none", fontFamily: "inherit", boxSizing: "border-box",
              appearance: "none", cursor: "pointer",
            }}
          >
            {FACTURE_CATS.map(c => <option key={c} value={c}>{c}</option>)}
          </select>

          {/* Bouton scan */}
          <button
            onClick={() => fileRef.current?.click()}
            style={{
              width: "100%", padding: "12px 0", borderRadius: 10, border: "none",
              background: form.img ? "#EAF3DE" : "#F0F0F0",
              color: form.img ? C.forest : C.muted,
              fontSize: 14, fontWeight: 700, cursor: "pointer",
              fontFamily: "inherit", marginBottom: form.img ? 8 : 14,
            }}
          >
            {form.img ? (t.photoChange || "✅ Photo prise — changer") : (t.prendrePhoto || "📷 Prendre une photo")}
          </button>

          {/* Aperçu miniature */}
          {form.img && (
            <div style={{ marginBottom: 14, borderRadius: 10, overflow: "hidden", maxHeight: 160 }}>
              <img src={form.img} alt="Aperçu" style={{ width: "100%", objectFit: "cover", display: "block" }} />
            </div>
          )}

          {/* Actions */}
          <div style={{ display: "flex", gap: 10 }}>
            <button
              onClick={() => { setShowForm(false); setForm({ date: todayISO(), categorie: FACTURE_CATS[0], img: null }); }}
              style={{
                flex: 1, padding: "11px 0", borderRadius: 10,
                border: `1.5px solid ${C.border}`, background: "transparent",
                color: C.muted, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
              }}
            >{t.annuler || "Annuler"}</button>
            <button
              onClick={handleSave}
              disabled={!form.img || saving}
              style={{
                flex: 2, padding: "11px 0", borderRadius: 10, border: "none",
                background: form.img ? C.forest : C.border,
                color: "#F8F6F1", fontWeight: 800, cursor: form.img ? "pointer" : "default",
                fontFamily: "inherit", opacity: saving ? 0.7 : 1,
              }}
            >
              {saving ? (t.sauvegarde || "Sauvegarde…") : (t.enregistrer || "Enregistrer")}
            </button>
          </div>
        </div>
      )}

      {/* Grille de miniatures */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "40px 20px", color: "rgba(255,255,255,0.7)", fontSize: 13 }}>
          {t.chargement || "Chargement…"}
        </div>
      ) : factures.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px 20px", color: "rgba(255,255,255,0.75)" }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📄</div>
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>{t.aucuneFacture || "Aucune facture"}</div>
          <div style={{ fontSize: 12 }}>Appuyez sur "{t.scanner || "+ Scanner une facture"}" pour commencer</div>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, paddingBottom: 16 }}>
          {factures.map(f => (
            <div key={f.id} style={{ borderRadius: 12, overflow: "hidden", background: "white" }}>
              {/* Image + overlay */}
              <div style={{ position: "relative" }}>
                <button
                  onClick={() => setViewItem(f)}
                  style={{
                    width: "100%", padding: 0, border: "none", cursor: "pointer",
                    background: C.border, display: "block",
                  }}
                >
                  <img
                    src={f.img}
                    alt=""
                    style={{ width: "100%", height: 130, objectFit: "cover", display: "block" }}
                  />
                </button>
                {/* Overlay gradient avec infos */}
                <div style={{
                  position: "absolute", bottom: 0, left: 0, right: 0,
                  background: "linear-gradient(transparent, rgba(0,0,0,0.72))",
                  padding: "20px 8px 8px",
                  pointerEvents: "none",
                }}>
                  <div style={{ fontSize: 11, fontWeight: 800, color: "white", letterSpacing: 0.2 }}>
                    {f.categorie || "Facture"}
                  </div>
                  {f.date && (
                    <div style={{ fontSize: 9, color: "rgba(255,255,255,0.7)", marginTop: 2 }}>
                      {fmtDate(f.date)}
                    </div>
                  )}
                </div>
              </div>
              {/* Bouton supprimer en dessous */}
              <button
                onClick={() => handleDelete(f.id)}
                style={{
                  width: "100%", padding: "8px 0",
                  background: confirmDeleteId === f.id ? "#DC2626" : "#FEF2F2",
                  border: "none", borderTop: "1px solid #FECACA",
                  color: confirmDeleteId === f.id ? "white" : "#DC2626",
                  fontSize: 12, fontWeight: 700,
                  cursor: "pointer", fontFamily: "inherit",
                  transition: "background 0.15s, color 0.15s",
                }}
              >
                {confirmDeleteId === f.id
                  ? (t.confirmerSuppression || "Confirmer ?")
                  : (t.supprimer || "🗑️ Supprimer")}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


const MAIN_TABS = [
  { id: "assurance", label: "Assurance" },
  { id: "garage",    label: "Garage"    },
  { id: "ct",        label: "CT"        },
  { id: "factures",  label: "Factures"  },
];

export default function DossiersScreen({ dossiers, setDossiers, depenses, userId, t = {} }) {
  const [mainTab,    setMainTab]    = useState("assurance");
  const [coordOpen,  setCoordOpen]  = useState({ assurance: false, garage: false });
  const [dirtyAssurance, setDirtyAssurance] = useState({});
  const [dirtyGarage,    setDirtyGarage]    = useState({});
  const [dirtyCt,        setDirtyCt]        = useState({});

  const update = (docId, field, val) =>
    setDossiers(prev => ({ ...prev, [docId]: { ...(prev[docId] || {}), [field]: val } }));

  const markDirty = (section, field) => {
    if (section === "assurance") setDirtyAssurance(prev => ({ ...prev, [field]: true }));
    if (section === "garage")    setDirtyGarage(prev => ({ ...prev, [field]: true }));
    if (section === "ct")        setDirtyCt(prev => ({ ...prev, [field]: true }));
  };

  const clearDirty = (section, field) => {
    if (section === "assurance") setDirtyAssurance(prev => ({ ...prev, [field]: false }));
    if (section === "garage")    setDirtyGarage(prev => ({ ...prev, [field]: false }));
    if (section === "ct")        setDirtyCt(prev => ({ ...prev, [field]: false }));
  };

  const save = (docId) =>
    setCoordOpen(p => ({ ...p, [docId]: false }));

  const da = dossiers.assurance || {};
  const dg = dossiers.garage    || {};
  const dc = dossiers.ct        || {};

  const statusA = getStatus(da.date);
  const statusG = getStatus(dg.date);
  const statusC = getStatus(dc.date);

  const urgents = [statusA, statusC]
    .filter(s => s && (s.color === "#C4622D" || s.color === "#EA7B20")).length;

  return (
    <div style={{ background: C.bg, minHeight: "calc(100vh - 118px)", paddingBottom: 24 }}>

      {/* Header + onglets principaux */}
      <div style={{ background: C.forest, paddingTop: 20 }}>
        <div style={{ padding: "0 20px 10px" }}>
          <div style={{ fontSize: 22, fontWeight: 900, color: "#F8F6F1", textAlign: "center" }}>{t.dossiers_titre || "Dossiers"}</div>
          <div style={{ fontSize: 12, color: "rgba(248,246,241,0.65)", marginTop: 3, textAlign: "center" }}>
            {urgents > 0
              ? `${urgents} ${t.documentsARenouveler || "document(s) à renouveler"}`
              : (t.documentsVehicule || "Documents du véhicule")}
          </div>
        </div>
        <div style={{ display: "flex", padding: "0 10px" }}>
          {MAIN_TABS.map(tab => {
            const active = mainTab === tab.id;
            const label = { assurance: t.assurance, garage: t.garage, ct: t.ct, factures: t.factures }[tab.id] || tab.label;
            return (
              <button
                key={tab.id}
                onClick={() => setMainTab(tab.id)}
                style={{
                  flex: 1, padding: "9px 4px 10px",
                  borderRadius: "8px 8px 0 0", border: "none",
                  borderBottom: active ? "3px solid #2D5A3D" : "3px solid #C4622D",
                  background: active ? "#2D5A3D" : "#C4622D",
                  color: "white",
                  fontSize: 13, fontWeight: active ? 800 : 600,
                  cursor: "pointer", fontFamily: "inherit",
                  transition: "color 0.15s, border-color 0.15s",
                }}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Assurance ── */}
      {mainTab === "assurance" && (
        <div style={{ padding: "14px 16px 0" }}>
          <CoordCard
            title={t.coordonnees || "Coordonnées"}
            summary={da.nom || ""}
            open={coordOpen.assurance}
            onToggle={() => setCoordOpen(p => ({ ...p, assurance: !p.assurance }))}
            onSave={() => save("assurance")}
            t={t}
          >
            <Field
              label={t.nomAssurance || "Nom de l'assurance"}
              value={da.nom || ""}
              placeholder={t.exAssurance || "Ex : MAIF, AXA, Allianz…"}
              dirty={dirtyAssurance.nom}
              onChange={v => {
                update("assurance", "nom", v);
                markDirty("assurance", "nom");
              }}
              onValidate={() => clearDirty("assurance", "nom")}
            />
            <Field
              label={t.adresse || "Adresse"}
              value={da.adresse || ""}
              placeholder={t.exAdresse || "Ex : 12 rue de la Paix, Paris"}
              dirty={dirtyAssurance.adresse}
              onChange={v => {
                update("assurance", "adresse", v);
                markDirty("assurance", "adresse");
              }}
              onValidate={() => clearDirty("assurance", "adresse")}
            />
            <Field
              label={t.numeroContrat || "Numéro de contrat"}
              value={da.contrat || ""}
              placeholder={t.exContrat || "Ex : 12345678"}
              dirty={dirtyAssurance.contrat}
              onChange={v => {
                update("assurance", "contrat", v);
                markDirty("assurance", "contrat");
              }}
              onValidate={() => clearDirty("assurance", "contrat")}
            />
            <Field
              label={t.numereTelephone || "Numéro de téléphone"}
              value={da.tel || ""}
              placeholder={t.exTel || "Ex : 0800 000 000"}
              type="tel"
              dirty={dirtyAssurance.tel}
              onChange={v => {
                update("assurance", "tel", v);
                markDirty("assurance", "tel");
              }}
              onValidate={() => clearDirty("assurance", "tel")}
            />
          </CoordCard>

          <CallButton tel={da.tel} label={t.appelerAssurance || "Appeler l'assurance"} />

          <DateCard
            label={t.echeanceLabel || "Échéance"}
            dateVal={da.date || ""}
            dirty={dirtyAssurance.date}
            onChange={v => {
              update("assurance", "date", v);
              markDirty("assurance", "date");
            }}
            onValidate={() => clearDirty("assurance", "date")}
            status={statusA}
            t={t}
          />
        </div>
      )}

      {/* ── Garage ── */}
      {mainTab === "garage" && (
        <div style={{ padding: "14px 16px 0" }}>
          <CoordCard
            title={t.coordonnees || "Coordonnées"}
            summary={dg.nom || ""}
            open={coordOpen.garage}
            onToggle={() => setCoordOpen(p => ({ ...p, garage: !p.garage }))}
            onSave={() => save("garage")}
            t={t}
          >
            <Field
              label={t.nomGarage || "Nom du garage"}
              value={dg.nom || ""}
              placeholder={t.exGarage || "Ex : Garage Dupont"}
              dirty={dirtyGarage.nom}
              onChange={v => {
                update("garage", "nom", v);
                markDirty("garage", "nom");
              }}
              onValidate={() => clearDirty("garage", "nom")}
            />
            <Field
              label={t.adresse || "Adresse"}
              value={dg.adresse || ""}
              placeholder={t.exAdresseGarage || "Ex : 12 avenue du Maréchal, Lyon"}
              dirty={dirtyGarage.adresse}
              onChange={v => {
                update("garage", "adresse", v);
                markDirty("garage", "adresse");
              }}
              onValidate={() => clearDirty("garage", "adresse")}
            />
            <Field
              label={t.numereTelephone || "Numéro de téléphone"}
              value={dg.tel || ""}
              placeholder={t.exTelGarage || "Ex : 04 00 00 00 00"}
              type="tel"
              dirty={dirtyGarage.tel}
              onChange={v => {
                update("garage", "tel", v);
                markDirty("garage", "tel");
              }}
              onValidate={() => clearDirty("garage", "tel")}
            />
          </CoordCard>

          <CallButton tel={dg.tel} label={t.appelerGarage || "Appeler le garage"} />

          <div style={{
            background: "white", borderRadius: 14, padding: "16px",
            boxShadow: "0 1px 8px rgba(44,90,61,0.07)",
            border: "1.5px solid rgba(44,90,61,0.08)",
          }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: C.dark, letterSpacing: 0.5, marginBottom: 12 }}>
              {t.prochaineRevision || "PROCHAINE RÉVISION"}
            </div>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: 0.8, marginBottom: 5 }}>
              {t.kilometrage || "KILOMÉTRAGE"}
            </div>
            <input
              type="number"
              value={dg.kmRevision || ""}
              onChange={e => {
                update("garage", "kmRevision", e.target.value);
                markDirty("garage", "kmRevision");
              }}
              placeholder={t.exKmRevision || "Ex : 65 000"}
              style={{
                width: "100%", padding: "10px 12px",
                borderRadius: 10, border: `1.5px solid ${C.border}`,
                background: "#F8F6F1", color: C.text,
                fontSize: 14, outline: "none",
                fontFamily: "inherit", boxSizing: "border-box",
                marginBottom: 8,
              }}
            />
            {dirtyGarage.kmRevision && (
              <ValidateButton onClick={() => clearDirty("garage", "kmRevision")} />
            )}
            {dg.kmRevision && (
              <div style={{ fontSize: 11, fontWeight: 600, color: "#2D9E4A" }}>
                {t.prochaineRevisionA || "Prochaine révision à"} {Number(dg.kmRevision).toLocaleString("fr-FR")} km
              </div>
            )}
          </div>

        </div>
      )}

      {/* ── Contrôle technique ── */}
      {mainTab === "ct" && (
        <div style={{ padding: "14px 16px 0" }}>
          <DateCard
            label={t.controleTechnique || "Contrôle technique"}
            dateVal={dc.date || ""}
            dirty={dirtyCt.date}
            onChange={v => {
              update("ct", "date", v);
              markDirty("ct", "date");
            }}
            onValidate={() => clearDirty("ct", "date")}
            status={statusC}
            t={t}
          />
        </div>
      )}

      {/* ── Factures scannées ── */}
      {mainTab === "factures" && (
        <TabFactures depenses={depenses} userId={userId} t={t} />
      )}
    </div>
  );
}
